import React from 'react';
import { cn } from '@/lib/utils';

export interface ContainerProps {
  children: React.ReactNode;
  /** Container size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /** Enable responsive padding */
  padding?: boolean;
  /** Custom padding override */
  customPadding?: string;
  /** Center the container */
  centered?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** HTML element type */
  as?: Exclude<keyof JSX.IntrinsicElements, 'stripe-buy-button'>;
}

/**
 * Responsive container component with breakpoint handling
 *
 * Breakpoints:
 * - Mobile: < 768px
 * - Tablet: 768px - 1024px
 * - Desktop: > 1024px
 * - Wide: > 1440px
 *
 * Usage:
 * ```tsx
 * <Container size="lg" padding>
 *   <p>Content goes here</p>
 * </Container>
 * ```
 */
export const Container: React.FC<ContainerProps> = ({
  children,
  size = 'xl',
  padding = true,
  customPadding,
  centered = true,
  className,
  as: Component = 'div',
  ...props
}) => {
  // Size variants based on design system breakpoints
  const sizeClasses = {
    sm: 'max-w-screen-sm', // 640px
    md: 'max-w-screen-md', // 768px
    lg: 'max-w-screen-lg', // 1024px
    xl: 'max-w-screen-xl', // 1280px
    '2xl': 'max-w-screen-2xl', // 1536px
    full: 'max-w-full',
  };

  // Responsive padding using design tokens
  const paddingClasses =
    padding && !customPadding
      ? 'px-4 sm:px-6 lg:px-8' // 16px mobile, 24px tablet, 32px desktop
      : customPadding || '';

  return (
    <Component
      className={cn(
        // Base container styles
        'w-full',

        // Size constraints
        sizeClasses[size],

        // Centering
        centered && 'mx-auto',

        // Responsive padding
        paddingClasses,

        // Custom classes
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

export default Container;
